import React, { useCallback, useEffect, useState } from 'react';
import useMindMapService from '../../hooks/useMindMapService';
import ReactFlow, {
    MiniMap,
    Controls,
    Background,
    addEdge,
    ConnectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';
import EditNodeModal from '../EditDrawer/index';
import { nodeTypes } from "../Shapes/shapes";
import { v4 } from "uuid";
import { MindMapContext } from '../../Context/contex';
import { MindMapAPI, workwiseServices, templateService } from '../../Api/api';
import { useParams } from "react-router-dom";
import { emitEdgeAdd, emitEdgeDelete, emitNodeAdd, emitNodeDelete, emitNodeUpdate, emitRoomID } from '../../Socket/socket';
import { processTemplateData, removeTemplateIdFromURL, updateFileSize } from '../../Utils/helper';
import Sidebar from '../Sidebar';
import WaterMark from '../WaterMark';
import { message } from 'antd';
import Loader from '../SkeletonLoader/Loader';

const MindMapContainer = ({ contentLoading, setContentLoading }) => {
    const {
        isReadOnly,
        setSelectedNodeId,
        nodes, setNodes,
        edges, setEdges,
        onEdgesChange, onNodesChange,
        selectedNodeId,
        reactFlowInstance, setReactFlowInstance,
        reactFlowWrapper,
        userInfo, userColor, setUserColor, setSavingData,
        isDocumentChanged, setIsDocumentChanged, setIsExternalAllow,
        documentInfo, isTemplateDocument, setTemplateData
    } = MindMapContext();

    const mindMapService = useMindMapService();

    const isMobile = window.screen.width < 768
    const { documentId } = useParams();
    const [nodeName, setNodeName] = useState('');
    const [nodeBg, setNodeBg] = useState('#9ca8b3');
    const [nodeHidden, setNodeHidden] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedShape, setSelectedShape] = useState('default');
    const [textBg, setTextBg] = useState('#000');
    const [nodePosition, setNodePosition] = useState({});
    const [selectedNode, setSelectedNode] = useState({})
    const [selectedEdgeId, setSelectedEdgeId] = useState(null);

    const initializeEmptyMap = async () => {
        const defaultNodeID = `default-node-${v4()}`;

        const initialNodeData = {
            id: defaultNodeID,
            label: "",
            nodeBg: "#fff",
            textBg: "#000",
            allocatedColor: userColor,
        };

        const initialNode = {
            id: defaultNodeID,
            type: "custom",
            data: initialNodeData,
            position: { x: 650, y: 250 },
        };

        setNodes([initialNode]);

        const nodePayload = {
            creatorId: userInfo.userId,
            id: defaultNodeID,
            documentId,
            name: nodeName,
            type: "custom",
            position: { x: 650, y: 250 },
            data: initialNodeData,
        };

        await mindMapService.createNode(nodePayload, {
            nodes: [initialNode],
            edges: [],
            isExternal: false,
        });

        emitNodeAdd(nodePayload);
        updateFileSize(nodePayload, setSavingData);
    };

    const hydrateExistingMap = (mindMapData) => {
        const nodesWithDimensions = mindMapData.nodes.map((node) => ({
            ...node,
            data: {
                ...node.data,
                width: node.style?.width,
                height: node.style?.height,
            },
            position: {
                x: node.position?.x || 10,
                y: node.position?.y || 10,
            },
        }));

        setNodes(nodesWithDimensions);
        setEdges(mindMapData.edges);

        const userNode = mindMapData.nodes.find(
            (node) => node.creatorId === userInfo.userId
        );

        if (userNode) {
            setUserColor(userNode.data.allocatedColor);
        }

        updateFileSize(mindMapData, setSavingData);
    };

    useEffect(() => {
        emitRoomID({ documentId });
    }, [documentId]);

    useEffect(() => {
        if (!documentId || isTemplateDocument === null) return;

        const loadMindMapData = async () => {
            setContentLoading(true);

            const searchParams = new URLSearchParams(window.location.search);
            const templateId = searchParams.get("templateId");

            let mindMapData = null;

            // If this is a template flow, fetch template first (prefer query param over path param)
            if (templateId || isTemplateDocument) {
                const templateToFetch = templateId || documentId;
                try {
                    const res = await templateService.fetchTemplateById(templateToFetch);
                    const processedData = processTemplateData(res);
                    mindMapData = processedData?.mindmapData || null;
                    if (isTemplateDocument) {
                        setTemplateData?.(processedData);
                    }
                } catch (err) {
                    console.error("Template fetch failed:", err);
                    message.error("Failed to fetch template data");
                    setContentLoading(false);
                    return;
                }
                // If templateId came from URL → remove it from the url
                if (templateId) {
                    try {
                        removeTemplateIdFromURL();
                        const importPayload = {
                            documentId,
                            nodes: mindMapData?.nodes ?? [],
                            edges: mindMapData?.edges ?? [],
                        };
                        try {
                            await mindMapService.saveImportDataToDb(importPayload);
                        } catch (err) {
                            console.error("Failed to save imported template data to DB:", err);
                        }
                    } catch (err) {
                        console.warn('Failed to remove templateId from URL (non-fatal):', err);
                    }
                }
            } else {
                // Normal document fetch
                try {
                    const res = await MindMapAPI.fetchMindMapById(documentId);
                    mindMapData = res?.data || null;
                } catch (err) {
                    console.error("Mind map fetch failed:", err);
                    message.error("Failed to fetch mind map data");
                    setContentLoading(false);
                    return;
                }
            }
            // Safety Fallback
            if (!mindMapData) {
                console.error("Mind map data not found after fetch");
                message.error("Mind map data not found");
                setContentLoading(false);
                return;
            }
            // Continue normal processing
            try {
                setIsExternalAllow(mindMapData?.isExternal);
                // Empty Map Handling
                if (!mindMapData?.nodes?.length) {
                    await initializeEmptyMap();
                } else {
                    hydrateExistingMap(mindMapData);
                }
            } catch (err) {
                console.error("Error processing mind map data:", err);
                message.error("Failed to process mind map data");
            } finally {
                setContentLoading(false);
            }
        };

        loadMindMapData();

    }, [documentId, isTemplateDocument]);

    const onConnect = useCallback((params) => {
        if (params.source === params.target) {
            return;
        }
        const newEdgeId = "edge-" + v4();
        const newEdge = { id: newEdgeId, documentId: documentId, ...params };
        const updatedEdges = addEdge(newEdge, edges);
        setEdges(updatedEdges);
        mindMapService.createEdge(newEdge, { nodes, edges: updatedEdges });
        emitEdgeAdd(newEdge);
        setIsDocumentChanged(true);
    }, [setEdges, edges, nodes, mindMapService]);

    const updateNodeDimensions = (nodeId, dimensions) => {
        reactFlowWrapper.current[nodeId] = dimensions;
    };
    useEffect(() => {
        const nodeElements = document.querySelectorAll('.react-flow__node');
        nodeElements.forEach((nodeElement) => {
            const nodeId = nodeElement.getAttribute('data-id');
            const dimensions = nodeElement.getBoundingClientRect();
            updateNodeDimensions(nodeId, dimensions);
        });
    }, [nodes]);

    const handleNodeClick = (event, node) => {
        setSelectedNode(node);
        setSelectedNodeId(node.id);
        setNodeBg(node.data.nodeBg);
        setNodeName(node.data.label);
        setNodeHidden(node.hidden);
        setEditModalOpen(true);
        setSelectedShape(node.type);
        setTextBg(node.data.textBg);
    };
    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback((event) => {
        const newNodeId = "node-" + v4();
        const newNodeData = {
            id: newNodeId,
            label: '',
            nodeBg: '#fff',
            textBg: '#000',
            allocatedColor: userColor
        }
        event.preventDefault();
        const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
        const type = event.dataTransfer.getData('application/reactflow');
        const proximityThreshold = 100;
        if (typeof type === 'undefined' || !type) {
            return;
        }
        else {
            const position = reactFlowInstance.project({
                x: event.clientX - reactFlowBounds.left,
                y: event.clientY - reactFlowBounds.top,
            });
            let nearestNode = null;
            let minDistance = Number.MAX_VALUE;
            nodes.forEach((node) => {
                const nodeCenterX = node.position.x + node.width / 2;
                const nodeCenterY = node.position.y + node.height / 2;
                const distance = Math.sqrt(
                    Math.pow(position.x - nodeCenterX, 2) + Math.pow(position.y - nodeCenterY, 2)
                );
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestNode = node;
                }
            });
            if (nearestNode && nearestNode.id && minDistance <= proximityThreshold) {
                const newEdge = {
                    id: `edge-${v4()}`,
                    documentId: documentId,
                    source: nearestNode.id,
                    target: newNodeId,
                    sourceHandle: 'bottom',
                    targetHandle: 'top',
                };

                const newNodePosition = {
                    x: nearestNode.position.x,
                    y: nearestNode.position.y + nearestNode.height + 20,
                };
                const newNode = {
                    creatorId: userInfo.userId,
                    id: newNodeId,
                    position: newNodePosition,
                    data: newNodeData,
                    type: type,
                    // style: { zIndex: 999 },
                };
                const updatedNodes = [...nodes, newNode];
                const updatedEdges = [...edges, newEdge];
                setEdges(updatedEdges);
                setNodes(updatedNodes);
                emitEdgeAdd(newEdge);

                const payload = {
                    creatorId: userInfo.userId,
                    id: newNodeId,
                    documentId: documentId,
                    name: nodeName,
                    type: type,
                    position: newNodePosition,
                    data: newNodeData,
                };
                if (isTemplateDocument) {
                    mindMapService.syncTemplate({ nodes: updatedNodes, edges: updatedEdges });
                } else {
                    mindMapService.createNode(payload, { nodes: updatedNodes, edges: updatedEdges });
                    mindMapService.createEdge(newEdge, { nodes: updatedNodes, edges: updatedEdges });
                }
                emitNodeAdd(payload);
            }
            else {
                const newNode = {
                    creatorId: userInfo.userId,
                    id: newNodeId,
                    position,
                    data: newNodeData,
                    type: type,
                    // style: { zIndex: 999 },
                };
                const updatedNodes = [...nodes, newNode];
                setNodes(updatedNodes);
                const payload = {
                    creatorId: userInfo.userId,
                    id: newNodeId,
                    documentId: documentId,
                    name: nodeName,
                    type: type,
                    position: position,
                    data: newNodeData,
                    // height: 100,
                    // dragging: false,
                    // selected: false,
                    // resizing: false,
                    // style: { width: 100, height: 100 },
                    // width: 100
                };
                mindMapService.createNode(payload, { nodes: updatedNodes, edges });
                emitNodeAdd(payload);
            }
            setIsDocumentChanged(true);
        }
    }, [reactFlowInstance, nodes, edges, setEdges, setNodes, mindMapService, isTemplateDocument]);

    const HandleNodeDrag = (event, node) => {
        if (node && node.position) {
            setNodePosition(node.position);
            setSelectedNodeId(node.id);
        }
    }
    useEffect(() => {
        if (selectedNodeId) {
            let payload = {
                position: nodePosition
            };
            // Build updated nodes to ensure the snapshot reflects the new position
            // even if the React state update hasn't propagated yet.
            const updatedNodes = nodes.map((n) =>
                n.id === selectedNodeId ? { ...n, position: nodePosition } : n
            );
            mindMapService.updateNode(selectedNodeId, payload, { nodes: updatedNodes, edges });

            let listerPayload = {
                id: selectedNodeId,
                position: nodePosition,
                documentId: documentId,
            }
            emitNodeUpdate(listerPayload);
            setIsDocumentChanged(true);
        }
    }, [nodePosition])

    const handelNodeDelete = (e, node) => {
        const filteredEdges = edges.filter(
            (e) => e.source !== selectedNodeId && e.target !== selectedNodeId
        );
        const filteredNodes = nodes.filter((n) => n.id !== selectedNodeId);
        mindMapService.deleteNodeWithEdges(selectedNodeId, edges, {
            nodes: filteredNodes,
            edges: filteredEdges,
        });
        setIsDocumentChanged(true);
        emitNodeDelete({ selectedNodeId: selectedNodeId, documentId: documentId, });
    }
    const handelEdgeDelete = (edge) => {
        const filteredEdges = edges.filter((e) => e.id !== selectedEdgeId);
        mindMapService.deleteEdge(selectedEdgeId, { nodes, edges: filteredEdges });
        emitEdgeDelete({ selectedEdgeId: selectedEdgeId, documentId: documentId, });
        setIsDocumentChanged(true);
    };

    useEffect(() => {
        const saveDocument = async () => {
            if (isDocumentChanged) {
                const data = {
                    nodes: nodes,
                    edges: edges,
                }
                workwiseServices.saveDocumentlastUpdate(documentId, setSavingData, data);
                setIsDocumentChanged(false);
            }
        };
        saveDocument();
    }, [nodes, edges, isDocumentChanged]);

    return (
        <>
            {contentLoading ? (
                <Loader />
            ) : (
                <div className="reactflow-wrapper" ref={reactFlowWrapper} style={{ width: '100vw', height: '90vh', backgroundColor: '#fff' }}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={nodeTypes}
                        onNodeDoubleClick={handleNodeClick}
                        onNodeClick={(e, node) => { setSelectedNodeId(node.id) }}
                        nodesDraggable={isReadOnly ? false : true}
                        nodesConnectable={isReadOnly ? false : true}
                        connectedOnClick={isReadOnly ? false : true}
                        deleteKeyCode={[isReadOnly ? null : 'Delete']}
                        connectionMode={ConnectionMode.Loose}
                        onInit={setReactFlowInstance}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onNodeDragStop={HandleNodeDrag}
                        onNodesDelete={handelNodeDelete}
                        onElementsRemove={handelNodeDelete}
                        onNodeMouseEnter={(e, node) => { setSelectedNodeId(node.id); }}
                        proOptions={{ hideAttribution: true }}
                        onEdgesDelete={handelEdgeDelete}
                        onEdgeClick={(e, node) => { setSelectedEdgeId(node.id); }}
                    >
                        {!isReadOnly && (
                            <div className='sideBarDragDropIcons'>
                                <Sidebar />
                            </div>
                        )}
                        <div className='mindMap-options'>
                            <Controls />
                            <MiniMap />
                        </div>
                        <Background variant="dots" gap={12} size={1} />
                    </ReactFlow>

                    {isReadOnly && userInfo.userId !== documentInfo?.creator?.id && (
                        <WaterMark />
                    )}
                </div>
            )}

            {!contentLoading && (
                <EditNodeModal
                    selectedNodeId={selectedNodeId}
                    nodes={nodes}
                    edges={edges}
                    setNodes={setNodes}
                    setEdges={setEdges}
                    setNodeName={setNodeName}
                    editModalOpen={editModalOpen}
                    setNodeBg={setNodeBg}
                    setNodeHidden={setNodeHidden}
                    nodeBg={nodeBg}
                    nodeHidden={nodeHidden}
                    nodeName={nodeName}
                    setEditModalOpen={setEditModalOpen}
                    setSelectedShape={setSelectedShape}
                    selectedShape={selectedShape}
                    setTextBg={setTextBg}
                    textBg={textBg}
                    selectedNode={selectedNode}
                />
            )}
        </>
    );
}

export default React.memo(MindMapContainer);